import Link from "next/link";
import Image from "next/image";
import CountryFlag from "@/components/ui/CountryFlag";
import { Calendar, CreditCard, Briefcase, Shield, TrendingUp, Landmark, Compass, Wallet, PiggyBank, FileText } from "lucide-react";
import { formatISODate } from "@/lib/daily/dateUtils";
import { SITE_AUTHOR } from "@/lib/constants/eeat";

interface PostImage {
  url: string;
  alt: string;
}

interface BlogPost {
  slug: string;
  title: string;
  summary: string;
  category: string;
  date?: string;
  author?: string;
  countryName?: string;
  countryCode?: string;
  images?: PostImage[];
  content?: string;
  /** Precomputed read time (server-side projection) — saves shipping content to cards. */
  readTimeMin?: number;
}

interface BlogCardProps {
  post: BlogPost;
  showReadMore?: boolean;
  priority?: boolean;
}

function estimateReadTime(content?: string): number {
  if (!content) return 5;
  const words = content.trim().split(/\s+/).length;
  return Math.max(3, Math.round(words / 200));
}

function formatBlogCardDate(dateStr: string): string {
  return formatISODate(dateStr, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

const COUNTRY_NAMES: Record<string, string> = {
  us: "United States",
  gb: "United Kingdom",
  au: "Australia",
  ca: "Canada",
  sg: "Singapore",
  in: "India",
  nz: "New Zealand",
  de: "Germany",
  ch: "Switzerland",
  nl: "Netherlands"
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  "Credit & Debt": <CreditCard size={14} />,
  "Employment & Salary Benefits": <Briefcase size={14} />,
  Insurance: <Shield size={14} />,
  Investments: <TrendingUp size={14} />,
  Loans: <Landmark size={14} />,
  Retirement: <Compass size={14} />,
  "Retirement & Pensions": <Wallet size={14} />,
  Savings: <PiggyBank size={14} />,
  Tax: <FileText size={14} />,
  "Tax & Employment": <FileText size={14} />,
};

export default function BlogCard({ post, showReadMore = true, priority = false }: BlogCardProps) {
  const heroImage = post.images?.[0];
  const readTime = post.readTimeMin ?? estimateReadTime(post.content);

  const resolvedCountryCode = post.countryCode?.toLowerCase();
  const resolvedCountryName = post.countryName || (resolvedCountryCode ? COUNTRY_NAMES[resolvedCountryCode] : undefined);

  return (
    <article className="card blog-card" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Featured Image */}
      {heroImage && (
        <Link 
          href={`/blog/${post.slug}`} 
          style={{ 
            display: "block", 
            width: "100%", 
            alignSelf: "stretch",
            overflow: "hidden", 
            borderRadius: "var(--radius-md)",
            marginBottom: "var(--space-4)",
            flexShrink: 0 
          }}
        >
          <div style={{ position: "relative", width: "100%", aspectRatio: "16/9" }}>
            <Image
              src={
                heroImage.url.includes("country-guides/")
                  ? heroImage.url.replace(".webp", "-thumb.webp").replace(".jpg", "-thumb.webp")
                  : heroImage.url
              }
              alt={heroImage.alt || post.title}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              priority={priority}
              loading={priority ? "eager" : "lazy"}
            />
          </div>
        </Link>
      )}

      {/* Card Body */}
      <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: "var(--space-3)", width: "100%" }}>
        {/* Badges */}
        <div className="blog-badges" style={{ margin: 0 }}>
          <span className="blog-category badge badge-secondary"><span style={{ display: "inline-flex", alignItems: "center", marginRight: "0.3rem", color: "var(--primary)" }}>{CATEGORY_ICONS[post.category]}</span>{post.category}</span>
          {resolvedCountryName && (
            <span className="blog-country badge badge-primary" style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
              {resolvedCountryCode && (
                <CountryFlag code={resolvedCountryCode} name={resolvedCountryName} className="country-flag-image-xs" />
              )}
              {resolvedCountryName}
            </span>
          )}
        </div>

        {/* Title */}
        <Link href={`/blog/${post.slug}`} className="blog-link" style={{ margin: 0 }}>
          <h3 className="post-title" style={{ margin: 0 }}>{post.title}</h3>
        </Link>

        {/* Summary */}
        <p className="post-summary" style={{ margin: 0, flex: 1 }}>{post.summary}</p>

        {/* Meta row: date + read time */}
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", fontSize: "var(--text-2xs)", color: "var(--text-muted, var(--text-secondary))", marginTop: "auto", paddingTop: "var(--space-2)", borderTop: "1px solid var(--border-color)" }}>
          {post.date && (
            <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <Calendar size={11} />
              {formatBlogCardDate(post.date)}
            </span>
          )}
          <span>{readTime} min read</span>
          <span>By <Link href="/authors/galvin-mendonca" className="author-name-link" style={{ color: 'inherit', textDecoration: 'underline' }}>{post.author || SITE_AUTHOR.name}</Link></span>
        </div>
      </div>
    </article>
  );
}
